<template>
<div class="sms-page">
  <v-card class="sms-card">
    <v-card-title class="sms-card-title">
      <v-icon left color="blue darken-3" size="26">mdi-message-text-outline</v-icon>
      <span>Send SMS</span>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text class="pt-4">

      <!-- Customer Search -->
      <v-autocomplete
        v-model="selectedCustomer"
        :items="customerList"
        :item-text="getCustomerLabel"
        item-value="id"
        return-object
        label="Search Customer"
        placeholder="Type a name to search..."
        clearable
        outlined
        dense
        prepend-inner-icon="mdi-account-search"
        no-data-text="No customers found"
        @change="onCustomerSelected"
        @click:clear="onCustomerCleared"
      ></v-autocomplete>

      <!-- To Number -->
      <v-text-field
        v-model="toNumber"
        label="To Number"
        placeholder="+923001234567"
        outlined
        dense
        prepend-inner-icon="mdi-phone"
        hint="Auto-filled from customer, or type manually (include country code)"
        persistent-hint
        class="mt-1"
      ></v-text-field>

      <!-- Message -->
      <v-textarea
        v-model="message"
        label="Message"
        placeholder="Type your message here..."
        outlined
        :no-resize="true"
        :rows="4"
        :counter="320"
        prepend-inner-icon="mdi-message-text"
        class="mt-3"
      ></v-textarea>

      <!-- Multi-part warning -->
      <div v-if="message && message.length > 160" class="caption orange--text darken-1 mb-1">
        <v-icon small color="orange darken-1">mdi-alert</v-icon>
        Message exceeds 160 characters — will be sent as {{ Math.ceil(message.length / 160) }} SMS parts.
      </div>

    </v-card-text>

    <v-card-actions class="px-4 pb-4">
      <v-btn
        text
        small
        @click="clearForm"
        :disabled="sending"
      >
        Clear
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn
        color="blue darken-3"
        dark
        depressed
        :loading="sending"
        :disabled="!toNumber || !message || sending"
        @click="sendSmsNow"
      >
        <v-icon left>mdi-send</v-icon>
        Send SMS
      </v-btn>
    </v-card-actions>
  </v-card>

  <!-- Snackbar -->
  <v-snackbar
    v-model="snackbar"
    :color="snackbarColor"
    :timeout="4000"
    top
    right
  >
    {{ snackbarText }}
    <template v-slot:action="{ attrs }">
      <v-btn text v-bind="attrs" @click="snackbar = false">Close</v-btn>
    </template>
  </v-snackbar>
</div>
</template>

<script>
import { smsService } from '../../services/sms.sevice'
import { mapState, mapActions } from 'vuex'

export default {
  name: 'SmsPage',

  computed: {
    ...mapState('customers', ['allCustomers']),

    customerList () {
      // allCustomers is { items: [...] } when loaded, or { loading: true } when loading
      if (this.allCustomers && this.allCustomers.items) {
        return this.allCustomers.items
      }
      return []
    }
  },

  data: () => ({
    selectedCustomer: null,
    toNumber: null,
    message: null,
    sending: false,
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success'
  }),

  created () {
    // Load all customers for the dropdown
    this.$store.dispatch('customers/getAll')
  },

  methods: {
    ...mapActions('customers', ['getAll']),

    getCustomerLabel (customer) {
      const name = [customer.name, customer.surname].filter(Boolean).join(' ')
      const phone = customer.mobileNumber || customer.contactNumber || 'No number'
      return `${name} — ${phone}`
    },

    onCustomerSelected (customer) {
      if (customer) {
        this.toNumber = customer.mobileNumber || customer.contactNumber || ''
      }
    },

    onCustomerCleared () {
      this.toNumber = null
      this.selectedCustomer = null
    },

    clearForm () {
      this.selectedCustomer = null
      this.toNumber = null
      this.message = null
    },

    async sendSmsNow () {
      if (!this.toNumber || !this.message) return
      this.sending = true
      try {
        let fromNumber = '+447576577516'
        const num = this.toNumber.startsWith('+') ? this.toNumber : '+' + this.toNumber
        if (!num.startsWith('+92')) {
          fromNumber = 'TravelCo'
        }
        await smsService.sendSms(this.toNumber, fromNumber, this.message)
        this.snackbarText = 'SMS sent successfully!'
        this.snackbarColor = 'success'
        this.message = null
      } catch (err) {
        this.snackbarText = `Failed to send: ${err}`
        this.snackbarColor = 'error'
      } finally {
        this.sending = false
        this.snackbar = true
      }
    }
  }
}
</script>

<style scoped>
.sms-page {
  padding: 16px;
}

.sms-card {
  max-width: 580px;
}

.sms-card-title {
  font-size: 18px;
  font-weight: 600;
  color: #37474f;
  padding: 16px 16px 12px 16px;
}
</style>